import _ from 'underscore';

import {ProcessesFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {evaluateLDFlag} from 'launchdarkly/LaunchDarklyUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import preferences from 'Preferences';
import licenses from 'user/Licenses';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

import {HOMEPAGE_LD_FLAG, HomepagePreferences} from './HomepageConstants';
import {MyWorkCategory} from './my-work/MyWorkConstants';

export function shouldShowNewHomepage(): boolean {
  return (
    (BenchlingConfig.NEW_HOMEPAGE_LA_OPT_IN || evaluateLDFlag(HOMEPAGE_LD_FLAG)) &&
    preferences.get(HomepagePreferences.SHOW_NEW_HOMEPAGE)
  );
}

/** Matches {@link HomeSidepanel} review-summary fetch and section visibility. */
export function hasReviewsLicense(): boolean {
  return licenses.hasLicense('LICENSE_REVIEWS');
}

/** Matches {@link HomeSidepanel} entry/worksheet review summary section visibility. */
export function hasReviewSummaryData(reviewSummary: ReviewSummary | null): boolean {
  return reviewSummary != null && !_.isEmpty(reviewSummary);
}

/** Matches {@link HomeSidepanel} assigned process task summary visibility. */
export function shouldShowAssignedProcessTasks(): boolean {
  return (
    isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) &&
    isModelTypeEnabledForListing(ModelTypeConstants.PROCESS_TASK)
  );
}

/** Matches {@link HomeSidepanel} recent worklists listing visibility. */
export function shouldShowWorklistsSection(): boolean {
  return (
    BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS &&
    isFeatureEnabled({feature: RegistryFeature.REGISTRY_WORKLISTS})
  );
}

export function shouldShowAISection(): boolean {
  return isAgentTypeEnabled(AgentType.ASSISTANT);
}

/** Matches {@link HomeSidepanel} onboarding checklist link visibility. */
export function shouldShowWelcomeSection(): boolean {
  return BenchlingConfig.ENABLE_NUE_MODULES;
}

export function getEnabledMyWorkCategories({
  entryReviewSummary = null,
  worksheetReviewSummary = null,
}: {
  entryReviewSummary?: ReviewSummary | null;
  worksheetReviewSummary?: ReviewSummary | null;
} = {}): Array<MyWorkCategory> {
  const categories: Array<MyWorkCategory> = [];
  if (hasReviewsLicense() && hasReviewSummaryData(entryReviewSummary)) {
    categories.push(MyWorkCategory.ENTRIES);
  }
  if (hasReviewsLicense() && hasReviewSummaryData(worksheetReviewSummary)) {
    categories.push(MyWorkCategory.WORKSHEETS);
  }
  if (shouldShowAssignedProcessTasks()) {
    categories.push(MyWorkCategory.TASKS);
  }
  return categories;
}

/** Coarse gate for rendering {@link MyWorkSection} in {@link Homepage}. */
export function shouldShowMyWorkSection(): boolean {
  return hasReviewsLicense() || shouldShowAssignedProcessTasks();
}
